/**
 * MetricCard Component
 *
 * Displays a metric value with optional trend indicator and sparkline.
 * Used for dashboard KPI displays in admin pages.
 */

import { TrendingUp, TrendingDown, Minus, ArrowUp, ArrowDown } from "lucide-react";
import type { ReactElement, ReactNode } from "react";

/** Data point for sparkline */
export interface SparklineDataPoint {
  readonly value: number;
  readonly timestamp?: string;
}

/** Trend direction */
export type TrendDirection = "up" | "down" | "flat";

/** Metric card props */
export interface MetricCardProps {
  /** Metric label/title */
  readonly label: string;
  /** Primary value to display */
  readonly value: string | number;
  /** Optional unit suffix (%, ms, etc.) */
  readonly unit?: string;
  /** Trend direction */
  readonly trend?: TrendDirection;
  /** Trend percentage or absolute change */
  readonly trendValue?: string | number;
  /** Whether positive trend is good (default: true for most metrics) */
  readonly positiveIsGood?: boolean;
  /** Sparkline data points */
  readonly sparkline?: readonly SparklineDataPoint[];
  /** Sparkline color */
  readonly sparklineColor?: "primary" | "success" | "warning" | "error";
  /** Icon to display */
  readonly icon?: ReactNode;
  /** Additional CSS classes */
  readonly className?: string;
  /** Loading state */
  readonly isLoading?: boolean;
  /** Compact mode for smaller cards */
  readonly compact?: boolean;
  /** Description text below value */
  readonly description?: string;
}

/** Get CSS classes based on trend and whether positive is good */
function getTrendClasses(
  trend: TrendDirection,
  positiveIsGood: boolean
): { text: string; icon: typeof TrendingUp } {
  if (trend === "flat") {
    return { text: "text-foreground-muted", icon: Minus };
  }

  const isGood = positiveIsGood ? trend === "up" : trend === "down";
  if (isGood) {
    return { text: "text-success", icon: trend === "up" ? TrendingUp : TrendingDown };
  }
  return { text: "text-error", icon: trend === "up" ? TrendingUp : TrendingDown };
}

/** Sparkline color classes */
const SPARKLINE_COLORS = {
  primary: "stroke-primary fill-primary/10",
  success: "stroke-success fill-success/10",
  warning: "stroke-warning fill-warning/10",
  error: "stroke-error fill-error/10",
};

/**
 * Render a simple sparkline SVG.
 */
function Sparkline({
  data,
  color = "primary",
  height = 32,
  width = 80,
}: {
  readonly data: readonly SparklineDataPoint[];
  readonly color?: "primary" | "success" | "warning" | "error";
  readonly height?: number;
  readonly width?: number;
}): ReactElement | null {
  if (data.length < 2) {
    return null;
  }

  const values = data.map((d) => d.value);
  const max = Math.max(...values);
  const min = Math.min(...values);
  const range = max - min || 1;

  // Create SVG path
  const points = data.map((d, i) => {
    const x = (i / (data.length - 1)) * width;
    const y = height - ((d.value - min) / range) * (height - 4) - 2;
    return { x, y };
  });

  const linePath = points.map((p, i) => `${i === 0 ? "M" : "L"}${p.x},${p.y}`).join(" ");
  const areaPath = `${linePath} L${width},${height} L0,${height} Z`;

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      className={`w-full ${SPARKLINE_COLORS[color]}`}
      style={{ height }}
      preserveAspectRatio="none"
    >
      <path d={areaPath} className="fill-current opacity-20" />
      <path d={linePath} className="stroke-current fill-none stroke-[1.5]" />
    </svg>
  );
}

/**
 * Loading skeleton for metric card.
 */
function MetricCardSkeleton({ compact }: { readonly compact?: boolean }): ReactElement {
  return (
    <div className={`card animate-pulse ${compact ? "p-3" : "p-4"}`}>
      <div className="h-4 w-20 bg-foreground-muted/20 rounded mb-2" />
      <div className={`${compact ? "h-6 w-16" : "h-8 w-24"} bg-foreground-muted/20 rounded mb-2`} />
      <div className="h-3 w-12 bg-foreground-muted/20 rounded" />
    </div>
  );
}

/**
 * MetricCard component for displaying KPI metrics.
 */
export function MetricCard({
  label,
  value,
  unit,
  trend,
  trendValue,
  positiveIsGood = true,
  sparkline,
  sparklineColor = "primary",
  icon,
  className = "",
  isLoading = false,
  compact = false,
  description,
}: MetricCardProps): ReactElement {
  if (isLoading) {
    return <MetricCardSkeleton compact={compact} />;
  }

  const trendConfig = trend ? getTrendClasses(trend, positiveIsGood) : null;
  const TrendIcon = trendConfig?.icon;

  return (
    <div className={`card ${compact ? "p-3" : "p-4"} ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <span className={`text-foreground-muted ${compact ? "text-xs" : "text-sm"}`}>{label}</span>
        {icon && <span className="text-foreground-muted flex-shrink-0">{icon}</span>}
      </div>

      {/* Value */}
      <div className="flex items-baseline gap-1">
        <span className={`font-bold ${compact ? "text-xl" : "text-2xl"}`}>
          {typeof value === "number" ? value.toLocaleString() : value}
        </span>
        {unit && <span className="text-sm text-foreground-muted">{unit}</span>}
      </div>

      {/* Trend */}
      {trend && trendConfig && (
        <div className={`flex items-center gap-1 mt-1 ${trendConfig.text}`}>
          {TrendIcon && <TrendIcon className="h-3.5 w-3.5" />}
          {trendValue && <span className="text-sm font-medium">{trendValue}</span>}
        </div>
      )}

      {/* Description */}
      {description && (
        <p className={`text-foreground-muted mt-1 ${compact ? "text-xs" : "text-sm"}`}>
          {description}
        </p>
      )}

      {/* Sparkline */}
      {sparkline && sparkline.length > 0 && (
        <div className="mt-3">
          <Sparkline data={sparkline} color={sparklineColor} height={compact ? 24 : 32} />
        </div>
      )}
    </div>
  );
}

/** Compact metric display for inline use */
export interface InlineMetricProps {
  /** Metric label */
  readonly label: string;
  /** Metric value */
  readonly value: string | number;
  /** Unit suffix */
  readonly unit?: string;
  /** Change direction */
  readonly change?: "up" | "down" | "none";
  /** Change value */
  readonly changeValue?: string | number;
  /** Additional CSS classes */
  readonly className?: string;
}

/**
 * InlineMetric for compact metric display in lists/tables.
 */
export function InlineMetric({
  label,
  value,
  unit,
  change,
  changeValue,
  className = "",
}: InlineMetricProps): ReactElement {
  const changeColor =
    change === "up" ? "text-success" : change === "down" ? "text-error" : "text-foreground-muted";

  const ChangeIcon = change === "up" ? ArrowUp : change === "down" ? ArrowDown : null;

  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <div>
        <p className="text-xs text-foreground-muted">{label}</p>
        <p className="text-sm font-semibold">
          {typeof value === "number" ? value.toLocaleString() : value}
          {unit && <span className="text-foreground-muted ml-0.5">{unit}</span>}
        </p>
      </div>
      {change && change !== "none" && changeValue && (
        <div className={`flex items-center ${changeColor}`}>
          {ChangeIcon && <ChangeIcon className="h-3 w-3" />}
          <span className="text-xs font-medium">{changeValue}</span>
        </div>
      )}
    </div>
  );
}

export default MetricCard;
